import { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

const AdminSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    brand_name: '',
    domain_name: '',
    sitemap_url: '',
    default_category: '',
    indexnow_key: '',
    prompt_template: '',
  });

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('blog_settings').select('*').eq('id', 1).maybeSingle();
      if (data) {
        setForm({
          brand_name: data.brand_name || '',
          domain_name: data.domain_name || '',
          sitemap_url: data.sitemap_url || '',
          default_category: data.default_category || '',
          indexnow_key: data.indexnow_key || '',
          prompt_template: data.prompt_template || '',
        });
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from('blog_settings').update(form).eq('id', 1);
    setSaving(false);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else toast({ title: 'Settings saved' });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>

        <Card>
          <CardContent className="space-y-5 pt-6">
            <div className="space-y-2">
              <Label>Brand name</Label>
              <Input value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Domain</Label>
              <Input value={form.domain_name} onChange={e => setForm({ ...form, domain_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Sitemap URL</Label>
              <Input value={form.sitemap_url} onChange={e => setForm({ ...form, sitemap_url: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Default category</Label>
              <Input value={form.default_category} onChange={e => setForm({ ...form, default_category: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>IndexNow key</Label>
              <Input value={form.indexnow_key} onChange={e => setForm({ ...form, indexnow_key: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Must match the file at https://{form.domain_name}/{form.indexnow_key}.txt
              </p>
            </div>
            <div className="space-y-2">
              <Label>Default prompt template</Label>
              <Textarea
                rows={18}
                className="font-mono text-xs"
                value={form.prompt_template}
                onChange={e => setForm({ ...form, prompt_template: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Tokens: {'{{keyword}}'}, {'{{lsi_keywords}}'}, {'{{brand_name}}'}, {'{{domain_name}}'}, {'{{sitemap_url}}'}
              </p>
            </div>

            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save settings
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
